// Billing: plan summary, payment method (managed in the Stripe portal; card
// details aren't exposed by the API) and invoices with PDF downloads.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { isForbiddenError } from '@app/lib/apiErrors';
import { getInvoices, getSubscription, openBillingPortal, type Invoice, type Subscription } from '@app/api/subscription';
import { Button, Card, EmptyState, ErrorState, PageHeader, SkeletonRows, StatusPill, useToast } from '@app/components/ui';
import { displayTierName } from '@app/utils/tierUtils';
import { logger } from '@app/utils/logger';

const money = (amount: number, currency: string) => new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount / 100);
const date = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const tone = (s: string) => {
  const v = s.toLowerCase();
  return v === 'paid' ? 'ok' : v === 'failed' || v === 'uncollectible' ? 'acc' : v === 'pending' || v === 'open' ? 'amb' : 'muted';
};

export const Billing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const churchId = user?.churchId;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [portal, setPortal] = useState(false);

  const load = async () => {
    if (!churchId) return;
    setLoading(true);
    setError(false);
    setForbidden(false);
    getSubscription(churchId).then(setSub).catch(() => undefined);
    try {
      const data = await getInvoices(churchId);
      setInvoices([...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (err: any) {
      if (err?.response?.status === 404) setInvoices([]);
      else if (isForbiddenError(err)) setForbidden(true);
      else {
        logger.error('Error loading invoices:', err);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  const manage = async () => {
    if (!churchId) return;
    setPortal(true);
    try {
      window.location.href = await openBillingPortal(churchId);
    } catch (err) {
      logger.error('Error opening billing portal:', err);
      toast('Could not open the billing portal', 'error');
      setPortal(false);
    }
  };

  const next = sub && sub.tier !== 'free' && !sub.cancelAtPeriodEnd && new Date(sub.currentPeriodEnd).getFullYear() > 1970 ? `Next charge ${date(sub.currentPeriodEnd)}` : 'Billing';

  if (forbidden) {
    return (
      <div className="max-w-[860px]">
        <PageHeader title="Billing" />
        <EmptyState title="Only church admins can see billing" body="Ask an admin of this church to give you access." />
      </div>
    );
  }

  return (
    <div className="max-w-[860px]">
      <PageHeader eyebrow={next} title="Billing" />
      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))' }}>
        <Card>
          <div className="text-[13px] text-t4">Plan</div>
          <div className="text-[20px] font-semibold mt-1.5">{displayTierName(sub?.tier || 'free')}</div>
          <Button variant="link" className="mt-3.5" onClick={() => navigate('/admin/subscription')}>
            Change plan
          </Button>
        </Card>
        <Card>
          <div className="text-[13px] text-t4">Payment method</div>
          {sub?.stripeCustomerId ? (
            <>
              <div className="text-[15px] font-semibold mt-1.5">Managed securely by Stripe</div>
              <Button variant="link" className="mt-3.5" loading={portal} onClick={manage}>
                Update payment method
              </Button>
            </>
          ) : (
            <div className="text-[14px] text-t3 mt-1.5">No card on file</div>
          )}
        </Card>
      </div>

      <h2 className="text-[13px] font-semibold text-t3 mt-[22px] mb-2.5">Invoices</h2>
      {error ? (
        <ErrorState title="Couldn't load invoices" body="Check your connection and try again." onRetry={load} retrying={loading} />
      ) : loading ? (
        <SkeletonRows rows={3} />
      ) : invoices.length === 0 ? (
        <EmptyState title="No invoices yet" body="Invoices show up here after your first payment." />
      ) : (
        <Card pad="8px 22px">
          <ul>
            {invoices.map((iv) => {
              const trial = iv.stripeInvoiceId?.startsWith('trial_');
              return (
                <li key={iv.id} className="flex flex-wrap items-center gap-x-3.5 gap-y-1 py-[13px] border-b border-line last:border-0 text-[14px]">
                  <span className="font-mono text-[13px] text-t3 w-[130px] truncate">{trial ? 'Trial' : iv.stripeInvoiceId?.slice(-10) || '–'}</span>
                  <span className="flex-1 min-w-[90px]">{date(iv.createdAt)}</span>
                  <StatusPill tone={tone(iv.status)}>{iv.status.charAt(0).toUpperCase() + iv.status.slice(1)}</StatusPill>
                  <span className="font-mono font-bold w-[84px] text-right">{money(iv.amount, iv.currency)}</span>
                  {iv.invoicePdf ? (
                    <a href={iv.invoicePdf} target="_blank" rel="noreferrer" className="text-link text-[13px] w-10 text-right" aria-label={`Download invoice from ${date(iv.createdAt)} as PDF`}>
                      PDF
                    </a>
                  ) : (
                    <span className="w-10" />
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
};
